import { useMemo, useState } from 'react';

import { Card, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import { formatInt } from '../utils/format';
import { IncidenceGroupBy, buildIncidenceRanking } from '../utils/incidences';
import { VisitSelection, stopMatchesSelection } from '../utils/selection';

const I18N_BASE = 'scenes.visit_history.incidences_card';
const CSS = 'visit-history-inc';

interface VisitIncidencesCardProps {
  stops: TiendaPerfectaVisitHistoryStop[];
  selection: VisitSelection;
  onSelect: (selection: NonNullable<VisitSelection>) => void;
}

/**
 * Top 5 incidents of the period by reason or by van. Like the material
 * totals, it recomputes from the stops the selection lets through.
 */
function VisitIncidencesCard({
  stops,
  selection,
  onSelect,
}: VisitIncidencesCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [groupBy, setGroupBy] = useState<IncidenceGroupBy>('type');

  const noReason = t(`${I18N_BASE}.no_reason`);
  const ranking = useMemo(
    () =>
      buildIncidenceRanking(
        stops.filter((stop) => stopMatchesSelection(stop, selection)),
        groupBy,
        noReason
      ),
    [stops, selection, groupBy, noReason]
  );

  // With no incidents there is no "top N" to announce.
  const total = formatInt(ranking.total, language);
  const subtitle =
    ranking.top.length === 0
      ? t(`${I18N_BASE}.subtitle_total`, { total })
      : t(`${I18N_BASE}.subtitle_${groupBy}`, {
          total,
          count: ranking.top.length,
        });

  const maxCount = ranking.top[0]?.count ?? 1;

  return (
    <Card
      className={CSS}
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          <span className={`${CSS}__sub`}>{subtitle}</span>
        </>
      }
      extra={
        <Segmented
          size="small"
          value={groupBy}
          onChange={(value) => setGroupBy(value as IncidenceGroupBy)}
          options={[
            { label: t(`${I18N_BASE}.by.type`), value: 'type' },
            { label: t(`${I18N_BASE}.by.van`), value: 'van' },
          ]}
        />
      }
    >
      {ranking.top.length === 0 ? (
        <div className={`${CSS}__empty`}>{t(`${I18N_BASE}.empty`)}</div>
      ) : (
        <ol className={`${CSS}__list`}>
          {ranking.top.map((row, index) => {
            const content = (
              <>
                <span className={`${CSS}__rank`}>{index + 1}</span>
                <span className={`${CSS}__name`} title={row.label}>
                  {row.label}
                </span>
                <span className={`${CSS}__count`}>
                  {formatInt(row.count, language)}
                </span>
                <span className={`${CSS}__pct`}>{row.pct}%</span>
                <span className={`${CSS}__bar`}>
                  <span style={{ width: `${(row.count / maxCount) * 100}%` }} />
                </span>
              </>
            );

            return (
              <li key={row.key}>
                {groupBy === 'van' ? (
                  <button
                    type="button"
                    className={`${CSS}__row ${CSS}__row--button`}
                    onClick={() => onSelect({ kind: 'van', vanId: row.key })}
                  >
                    {content}
                  </button>
                ) : (
                  <div className={`${CSS}__row`}>{content}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {ranking.restGroups > 0 && (
        <div className={`${CSS}__more`}>
          {t(`${I18N_BASE}.more_${groupBy}`, {
            groups: ranking.restGroups,
            incidents: formatInt(ranking.restCount, language),
          })}
        </div>
      )}
    </Card>
  );
}

export default VisitIncidencesCard;
